import { CameraIcon } from '../../components/ui/icons';
import { useEffect } from 'react';
import {
  useNavigation,
  useNavigationState,
} from '../navigation/NavigationProvider';
import {
  Badge,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Tooltip,
} from '@mui/material';
import { ResponsiveDialog } from '../../components/ui/ResponsiveDialog';
import { DialogHeader } from '../../components/ui/DialogHeader';
import { DialogContent } from '../../components/ui/DialogContent';

export interface CaptureQueueEntry {
  userId: string;
  collectionId: string;
  name: string;
  count: number;
  attention: number;
  open: () => void;
}

export type RegisterCaptureQueue = (
  userId: string,
  collectionId: string,
  entry?: CaptureQueueEntry,
) => void;

export function CaptureQueueBadge({ queues }: { queues: CaptureQueueEntry[] }) {
  const { replaceState } = useNavigation();
  const [choosing, setChoosing] = useNavigationState('capture.chooser', false);
  const active = queues.filter((queue) => queue.count > 0);
  const count = active.reduce((total, queue) => total + queue.count, 0);
  const attention = active.reduce((total, queue) => total + queue.attention, 0);
  useEffect(() => {
    if (!count) replaceState('capture.chooser', false);
  }, [count, replaceState]);
  if (!count) return null;
  const label = `Capture queue, ${count} ${count === 1 ? 'item' : 'items'}${attention ? `, ${attention} need${attention === 1 ? 's' : ''} attention` : ''}`;
  return (
    <>
      <Tooltip title={label}>
        <IconButton
          aria-label={label}
          aria-haspopup="dialog"
          onClick={() => {
            if (active.length === 1) active[0].open();
            else setChoosing(true);
          }}
          sx={{
            minWidth: 44,
            minHeight: 44,
            flexShrink: 0,
            color: 'primary.main',
          }}
        >
          <Badge
            badgeContent={count}
            max={99}
            color={attention ? 'warning' : 'primary'}
          >
            <CameraIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <ResponsiveDialog
        open={choosing && active.length > 0}
        onClose={() => setChoosing(false)}
        maxWidth="xs"
        aria-labelledby="capture-collections-title"
      >
        <DialogHeader
          id="capture-collections-title"
          title="Capture queue"
          onClose={() => setChoosing(false)}
        />
        <DialogContent>
          <List disablePadding>
            {active.map((queue) => (
              <ListItemButton
                key={queue.collectionId}
                onClick={() => {
                  queue.open();
                }}
              >
                <ListItemText
                  primary={queue.name}
                  secondary={`${queue.count} ${queue.count === 1 ? 'item' : 'items'}${queue.attention ? ` · ${queue.attention} need${queue.attention === 1 ? 's' : ''} attention` : ''}`}
                />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
      </ResponsiveDialog>
    </>
  );
}
